import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
// Native iOS shell (WS-2 auth, WS-3 entitlement). `platform.js` has zero
// imports of its own, and both native barrels reach their Capacitor plugin only
// through loadNative(), so no browser ever requests those chunks.
import { isNative } from '@/lib/platform';
import { startNativeOAuth } from '@/native/auth';
import { initPurchases, getNativeEntitlement, logOutPurchases } from '@/native/purchases';
import { isCompedEmail } from '@/config/compedAccounts';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [hadExpiredPromoTrial, setHadExpiredPromoTrial] = useState(false);

  // Check onboarding status and subscription when user changes
  useEffect(() => {
    if (user) {
      checkOnboardingStatus();
      checkSubscriptionStatus();
    } else {
      setHasCompletedOnboarding(false);
      setHasActiveSubscription(false);
      setSubscriptionLoading(false);
      // Sign out of RevenueCat too, and drop the on-device entitlement cache, so
      // the next account on this device starts clean. A shared family iPad is a
      // realistic case for this product.
      if (isNative()) { void logOutPurchases(); }
    }
  }, [user]);

  const checkOnboardingStatus = async () => {
    try {
      // Check user metadata (stored in Supabase auth)
      const completed = user?.user_metadata?.onboarding_completed;
      setHasCompletedOnboarding(completed ?? false);
    } catch (err) {
      console.error('Unexpected error checking onboarding:', err);
      setHasCompletedOnboarding(false);
    }
  };

  // Check if user has an active subscription
  const checkSubscriptionStatus = async () => {
    const localPremium = localStorage.getItem('ttt_has_plan') === 'premium';
    // The local flag is a per-device fallback, so remember whose plan set it.
    // Legacy flags have no email stored: those still count (missing = allow),
    // but once stamped, a different account on the same device cannot inherit it.
    const localPremiumOwner = (localStorage.getItem('ttt_has_plan_email') || '').toLowerCase().trim();
    setSubscriptionLoading(true);

    // Dev mode users bypass Supabase subscription check, trust localStorage
    if (user?.loginMethod === 'dev_mode') {
      setHasActiveSubscription(localPremium);
      setSubscriptionLoading(false);
      return localPremium;
    }

    if (!user?.email) {
      setHasActiveSubscription(false);
      setSubscriptionLoading(false);
      return false;
    }

    // Comped accounts (src/config/compedAccounts.js). Checked before the
    // Supabase read so their access never depends on the subscriptions RLS
    // policy returning their row. Keyed on the authenticated email, so the
    // address alone grants nothing without the account password.
    if (isCompedEmail(user.email)) {
      setHasActiveSubscription(true);
      setHadExpiredPromoTrial(false);
      setSubscriptionLoading(false);
      return true;
    }

    try {
      const emailNormalized = user.email.toLowerCase().trim();
      const { data, error } = await supabase
        .from('subscriptions')
        .select('status, current_period_end, user_email, updated_at, created_at, stripe_customer_id')
        .ilike('user_email', emailNormalized)
        .order('current_period_end', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error checking subscription:', error);
        // Fallback right after checkout before webhook/db sync catches up
        setHasActiveSubscription(localPremium);
        return localPremium;
      } else {
        // Subscriptions table can contain multiple rows per email. Consider any valid active row.
        //
        // Deliberately forgiving. A false positive here costs us one month of
        // access; a false negative locks a paying customer out of the app they
        // just bought, and they churn. Three real lockouts are handled below:
        //   1. 'past_due' - the card bounced once and Stripe is retrying. The
        //      subscription is still live, so keep the app open while it retries.
        //   2. current_period_end IS NULL - Stripe's 2026-01-28 API move of
        //      current_period_end made the webhook write NULLs onto real payers'
        //      rows. NULL means "we do not know the end date", not "expired".
        //   3. a renewal webhook we missed - the row silently ages out mid-month,
        //      so an expired row still counts for a short grace window.
        // Promo rows ('event_promo') keep the strict rule: no NULL tolerance and
        // no grace, otherwise a free event promo turns into permanent access.
        const validStatuses = ['active', 'trialing', 'subscribed', 'past_due'];
        const GRACE_MS = 3 * 24 * 60 * 60 * 1000;
        const now = new Date();
        const rows = Array.isArray(data) ? data : [];
        const isPromoRow = (row) => row?.stripe_customer_id === 'event_promo';
        const hasValidActive = rows.some((row) => {
          if (!validStatuses.includes((row?.status || '').toLowerCase())) return false;
          const endsAt = row?.current_period_end ? new Date(row.current_period_end).getTime() : NaN;
          if (Number.isNaN(endsAt)) return !isPromoRow(row);
          const cutoff = now.getTime() - (isPromoRow(row) ? 0 : GRACE_MS);
          return endsAt > cutoff;
        });

        // Promo-trial detection: any row stamped with 'event_promo' means this user
        // came in via a promo code at some point. If no row is currently active AND
        // a promo row exists with an expired window, treat as "promo trial ended"
        // so the route guard can send them back through onboarding.
        const promoRows = rows.filter(isPromoRow);
        const hasExpiredPromo =
          !hasValidActive &&
          promoRows.length > 0 &&
          promoRows.every((row) => !row?.current_period_end || new Date(row.current_period_end) <= now);
        setHadExpiredPromoTrial(hasExpiredPromo);

        // Zero rows is NOT proof that the user does not pay. The subscriptions
        // RLS policy has silently returned an empty set for real payers before
        // (case-sensitive email match, see supabase_subscriptions_rls_fix.sql),
        // and that read succeeds, so it never hits the error branch above. When
        // the read comes back empty we treat it as inconclusive and fall back to
        // the local flag, which we only ever set after a confirmed live sub.
        const inconclusive = rows.length === 0;
        const localPremiumForThisUser =
          localPremium && (!localPremiumOwner || localPremiumOwner === emailNormalized);
        const nextState = hasValidActive || (inconclusive && localPremiumForThisUser);
        if (!hasValidActive && nextState) {
          console.warn('[Auth] subscription read returned no rows for a user with a local premium flag - keeping access');
        }
        // Native only: StoreKit is a third, independent source of truth, ORed in
        // and never ANDed. An Apple payer must get in during the seconds before
        // the RevenueCat webhook writes their row, and a web Stripe payer opening
        // the iOS app has no StoreKit purchase at all and must still get in.
        // On the web isNative() is false, nativeActive stays null, and
        // nextStateWithNative === nextState by identity.
        let nativeActive = null;
        if (isNative() && user?.id) {
          await initPurchases(user.id);
          nativeActive = (await getNativeEntitlement(user.id))?.active ?? null;
        }
        const nextStateWithNative = nextState || nativeActive === true;

        setHasActiveSubscription(nextStateWithNative);
        if (hasValidActive) {
          localStorage.setItem('ttt_has_plan', 'premium');
          localStorage.setItem('ttt_has_plan_email', emailNormalized);
        } else if (!inconclusive && !isNative()) {
          // Only clear on positive evidence: rows came back and none of them is
          // live. Clearing on an empty result was logging payers out of their plan.
          //
          // The !isNative() guard: on iOS "rows came back and none is live" is
          // NOT positive evidence, because the live entitlement can be the
          // StoreKit one, whose row appears only once the RevenueCat webhook
          // lands. Clearing the flag there would revoke a purchase made seconds
          // ago. In every browser this guard is true, so the branch behaves
          // exactly as it does today.
          localStorage.removeItem('ttt_has_plan');
          localStorage.removeItem('ttt_has_plan_email');
        }
        return nextStateWithNative;
      }
    } catch (err) {
      console.error('Unexpected error checking subscription:', err);
      setHasActiveSubscription(localPremium);
      return localPremium;
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Mark onboarding as completed
  const markOnboardingComplete = async (extra = {}) => {
    if (!user) return { error: 'No user logged in' };

    try {
      // Update user metadata in Supabase auth.
      // `extra` lets onboarding persist answers it wants the backend to read later
      // (e.g. child_gender, used for LT/RU first-person gender agreement in AI replies).
      const { error: metadataError } = await supabase.auth.updateUser({
        data: { onboarding_completed: true, ...extra }
      });

      if (metadataError) {
        console.error('Error updating metadata:', metadataError);
        return { error: metadataError };
      }

      setHasCompletedOnboarding(true);
      return { success: true };
    } catch (err) {
      console.error('Error marking onboarding complete:', err);
      return { error: err };
    }
  };

  useEffect(() => {
    // Check if dev mode is enabled (Vite dev server or password-gated bypass)
    const isDevServer = import.meta.env.DEV;
    const storedUserRaw = localStorage.getItem('ttt_user_data');
    let storedDevParsed = null;
    let isStoredDevUser = false;
    try {
      if (storedUserRaw) {
        storedDevParsed = JSON.parse(storedUserRaw);
        isStoredDevUser = storedDevParsed?.loginMethod === 'dev_mode';
      }
    } catch {
      isStoredDevUser = false;
    }
    const devModeEnabled = sessionStorage.getItem('dev-mode-enabled') === 'true' || isStoredDevUser;

    /** Dev-only fake user: never on production builds so live testing always uses real Supabase auth. */
    const allowDevBypass =
      !import.meta.env.PROD &&
      (isDevServer || devModeEnabled) &&
      devModeEnabled;

    const onboardingCompleted = localStorage.getItem('ttt-onboarding-complete') === 'true';
    const localPremium = localStorage.getItem('ttt_has_plan') === 'premium';
    const devUser = {
      id: storedDevParsed?.id || `dev-user-${Date.now()}`,
      email: storedDevParsed?.email || 'developer@localhost',
      loginMethod: 'dev_mode',
      user_metadata: {
        onboarding_completed: onboardingCompleted,
        name: storedDevParsed?.displayName || storedDevParsed?.name || undefined,
      },
    };

    /** Real Supabase session must win over dev bypass (otherwise email/password sign-in never updates UI). */
    const applyDevUserIfEligible = () => {
      if (!allowDevBypass) {
        setUser(null);
        return;
      }
      setUser(devUser);
      setHasCompletedOnboarding(onboardingCompleted);
      setHasActiveSubscription(localPremium);
      setSubscriptionLoading(false);
    };

    const resolveUserFromSession = async () => {
      const {
        data: { session },
        error: sessionErr,
      } = await supabase.auth.getSession();
      console.log('[AuthContext] resolveUserFromSession: session=', !!session?.user, session?.user?.email ?? 'none', sessionErr ? `err=${sessionErr.message}` : '');

      if (session?.user) {
        if (window.location.hash.includes('access_token')) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        setSession(session);
        setUser(session.user);
        setLoading(false);
        return;
      }

      applyDevUserIfEligible();
      setLoading(false);
    };

    void resolveUserFromSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[AuthContext] onAuthStateChange:', event, '| user:', session?.user?.email ?? 'none');
      if (session?.user) {
        if (window.location.hash.includes('access_token')) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        // Pre-set subscriptionLoading=true so redirect guards (Login.jsx, PublicEntryRoute)
        // never fire with a stale subscriptionLoading=false before checkSubscriptionStatus runs.
        // React 18 batches these two setState calls into one render.
        setSubscriptionLoading(true);
        setSession(session);
        setUser(session.user);
        setLoading(false);
        return;
      }
      if (event === 'SIGNED_OUT') {
        void supabase.auth.getSession().then(({ data: { session: s2 } }) => {
          if (s2?.user) {
            setUser(s2.user);
            setLoading(false);
            return;
          }
          applyDevUserIfEligible();
          setLoading(false);
        });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Sign up with email
  const signUp = async (email, password, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { ...metadata, onboarding_completed: false } // New users start with onboarding pending
      }
    });
    return { data, error };
  };

  // Sign in with email
  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    // Native shell: the canonical-host redirect below would eject the user into
    // Safari, because the native origin is neither localhost nor
    // www.timohelps.com. It is not removed or weakened, it is simply never
    // reached on native, where PKCE has no cross-origin problem to solve: the
    // deep link is delivered back into this same webview, so the verifier is
    // already in the right localStorage.
    if (isNative()) {
      const r = await startNativeOAuth('google');
      if (r.handled) return { data: null, error: r.error };
    }
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const canonicalHost = 'www.timohelps.com';

    // PKCE stores the code verifier in localStorage on the current origin. If the user is
    // on timohelps.com (no-www), the verifier lives there, but the callback lands on
    // www.timohelps.com where it doesn't exist - exchange fails silently. Redirect to www first.
    if (!isLocalhost && window.location.hostname !== canonicalHost) {
      console.log('[Auth] Non-canonical domain, redirecting to www before OAuth');
      window.location.replace(`https://${canonicalHost}/login?google=1`);
      return { data: null, error: null };
    }

    const redirectTo = isLocalhost
      ? `${window.location.origin}/auth/callback`
      : `https://${canonicalHost}/auth/callback`;
    console.log('[Auth] Google OAuth redirectTo:', redirectTo);
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        queryParams: { prompt: 'select_account' },
      }
    });
    if (error) {
      console.error('[Auth] Google OAuth error:', error.message, error);
    } else {
      console.log('[Auth] Google OAuth initiated, provider URL:', data?.url);
    }
    return { data, error };
  };

  // Shared OAuth initiator for providers that use the same canonical-domain + PKCE
  // dance as Google (Facebook, Apple). `flagParam` is the query flag we re-add after
  // the non-www -> www redirect so Login.jsx can auto-resume the OAuth on the canonical host.
  const signInWithProvider = async (provider, flagParam, options = {}) => {
    // Same reasoning as signInWithGoogle. Covers Apple and Facebook in one place.
    if (isNative()) {
      const r = await startNativeOAuth(provider, options);
      if (r.handled) return { data: null, error: r.error };
    }
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const canonicalHost = 'www.timohelps.com';

    if (!isLocalhost && window.location.hostname !== canonicalHost) {
      console.log(`[Auth] Non-canonical domain, redirecting to www before ${provider} OAuth`);
      window.location.replace(`https://${canonicalHost}/login?${flagParam}=1`);
      return { data: null, error: null };
    }

    const redirectTo = isLocalhost
      ? `${window.location.origin}/auth/callback`
      : `https://${canonicalHost}/auth/callback`;
    console.log(`[Auth] ${provider} OAuth redirectTo:`, redirectTo);
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo, ...options },
    });
    if (error) {
      console.error(`[Auth] ${provider} OAuth error:`, error.message, error);
    } else {
      console.log(`[Auth] ${provider} OAuth initiated, provider URL:`, data?.url);
    }
    return { data, error };
  };

  // Sign in with Facebook (Meta). Covers the Instagram/Facebook audience.
  const signInWithFacebook = async () => signInWithProvider('facebook', 'facebook');

  // Sign in with Apple. High-trust on iOS, which is most of our traffic.
  const signInWithApple = async () => signInWithProvider('apple', 'apple');

  // Sign out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut({ scope: 'global' });
    if (!error) {
      setSession(null);
      setHasCompletedOnboarding(false);
      setHasActiveSubscription(false);
      localStorage.removeItem('ttt_has_plan');
      localStorage.removeItem('ttt_has_plan_email');
    }
    return { error };
  };

  const value = {
    user,
    session,
    loading,
    hasCompletedOnboarding,
    markOnboardingComplete,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithFacebook,
    signInWithApple,
    signOut,
    hasActiveSubscription,
    subscriptionLoading,
    checkSubscriptionStatus,
    hadExpiredPromoTrial
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
